import React from "react";
import { Navigate, NavLink, Outlet, useLocation } from "react-router-dom";
import { PaymentBanner } from "@/components/PaymentBanner";
import { TrialBanner } from "@/components/TrialBanner";
import { BarChart3, FileQuestion, LayoutDashboard, Loader2, MapPin, Store, UserCog, Users, KanbanSquare } from "lucide-react";
import { LogoutButton } from "@/components/LogoutButton";
import { useAuth } from "@/lib/auth";
import { BrandMark } from "@/components/Brand";

export const NAV = [
  { to: "/dashboard", label: "Overview", icon: LayoutDashboard, end: true },
  { to: "/dashboard/leads", label: "Leads", icon: KanbanSquare },
  { to: "/dashboard/events", label: "Events & booths", icon: MapPin },
  { to: "/dashboard/profile", label: "Profile & assets", icon: Store },
  { to: "/dashboard/forms", label: "Quote forms", icon: FileQuestion },
  { to: "/dashboard/analytics", label: "Analytics", icon: BarChart3 },
  { to: "/dashboard/team", label: "Team", icon: Users },
  { to: "/dashboard/account", label: "Account", icon: UserCog, ownerOnly: true },
];

const linkCls = ({ isActive }: { isActive: boolean }) =>
  `flex items-center gap-3 whitespace-nowrap rounded-sm px-3 py-2 text-sm font-medium transition-colors ${isActive ? "bg-emerald-50 text-emerald-700" : "text-muted-foreground hover:bg-muted hover:text-foreground"}`;

export default function Shell() {
  const { vendor } = useAuth();
  const loc = useLocation();
  if (vendor === undefined) return <div className="grid min-h-screen place-items-center bg-background"><Loader2 className="h-6 w-6 animate-spin text-emerald-600" /></div>;
  if (!vendor) return <Navigate to="/login" replace />;
  const plan = vendor.plan;
  const isOwner = vendor.user.staff_role === "owner";
  const locked = vendor.vendor.trial?.state === "expired";
  if (locked && isOwner && !loc.pathname.startsWith("/dashboard/account")) return <Navigate to="/dashboard/account" replace />;

  return (
    <div className="min-h-screen bg-background text-foreground lg:flex">
      <aside className="sticky top-0 z-20 border-b border-border bg-white/95 backdrop-blur lg:h-screen lg:w-64 lg:shrink-0 lg:border-b-0 lg:border-r">
        <div className="flex items-center justify-between px-4 py-4 lg:px-5">
          <BrandMark />
          <LogoutButton kind="vendor" testid="vendor-logout-button-mobile" className="lg:hidden" />
        </div>
        <nav className="flex gap-1 overflow-x-auto px-3 pb-3 lg:flex-col lg:px-3">
          {NAV.filter((n) => !n.ownerOnly || vendor.user.staff_role === "owner").map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} className={linkCls} data-testid={`nav-${n.label.split(" ")[0].toLowerCase()}`}>
              <n.icon className="h-4 w-4" /> {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="absolute bottom-0 hidden w-full border-t border-border p-4 lg:block">
          <p className="truncate text-sm font-semibold" data-testid="vendor-company-name">{vendor.vendor.name}</p>
          <p className="truncate text-xs text-muted-foreground">{vendor.user.email} · {vendor.user.staff_role}</p>
          <div className="mt-3 flex items-center justify-between">
            <span className="rounded-sm bg-emerald-50 px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider text-emerald-700" data-testid="vendor-plan-badge">{plan.name}</span>
            <LogoutButton kind="vendor" testid="vendor-logout-button" />
          </div>
        </div>
      </aside>
      <main className="min-w-0 flex-1 p-4 sm:p-6 lg:p-8">
        <TrialBanner trial={vendor.vendor.trial} isOwner={isOwner} />
        <PaymentBanner issue={vendor.vendor.payment_issue} isOwner={isOwner} />
        {locked && !isOwner ? null : <Outlet />}
      </main>
    </div>
  );
}

export const PageHead = ({ eyebrow, title, children }: { eyebrow: string; title: string; children?: React.ReactNode }) => (
  <div className="mb-6 flex flex-wrap items-end justify-between gap-4 rise">
    <div>
      <p className="text-xs font-bold uppercase tracking-[0.2em] text-primary">{eyebrow}</p>
      <h1 className="mt-1 text-3xl font-semibold tracking-tight sm:text-4xl">{title}</h1>
    </div>
    <div className="flex flex-wrap gap-2">{children}</div>
  </div>
);

export const Panel = ({ children, className = "", ...rest }: any) => (
  <div className={`hard-shadow rounded-2xl border border-border bg-card p-5 ${className}`} {...rest}>{children}</div>
);
